import React, { useEffect } from 'react';
import { Box, Container, Link, Flex, Button, useColorModeValue, Icon, Divider } from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaHome } from 'react-icons/fa';
import MarkdownViewer from '../components/MarkdownViewer';
import AppHeader from '../components/AppHeader';
import { useLyricsTheme } from '../context/LyricsThemeContext';

interface StaticPageProps {
  page?: string;
  filePath?: string;
  title?: string;
  backLink?: string;
  backText?: string;
}

const StaticPage: React.FC<StaticPageProps> = ({
  page,
  filePath,
  title,
  backLink = '/',
  backText = 'Back to Home'
}) => {
  const location = useLocation();
  
  // Determine the file path based on the page prop
  const markdownPath = page ? `/markdown/${page}.md` : (filePath || '/markdown/default.md');
  
  // Determine the title if not provided
  const pageTitle = title || (page ? page.charAt(0).toUpperCase() + page.slice(1) : 'Documentation');
  
  const { colorMode } = useLyricsTheme();
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const accentColor = useColorModeValue('green.500', 'green.300');
  const cardBgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Force body to be scrollable
  useEffect(() => {
    // Ensure the body has proper overflow settings
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    
    return () => {
      // Clean up when component unmounts
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, []);

  useEffect(() => {
    console.log('StaticPage render:', { 
      page, 
      resolvedPath: markdownPath,
      currentRoute: location.pathname
    });
  }, [page, markdownPath, location.pathname]);

  return (
    <Box 
      bg={bgColor} 
      height="100%"
      minHeight="100%"
      position="relative"
      display="flex"
      flexDirection="column"
      overflow="visible"
    >
      <AppHeader
        isCondensed={true}
        pageTitle={pageTitle}
        accentColor={accentColor}
      />
      
      <Container maxW="container.xl" py={16} px={[4, 6, 8]} flexGrow={1}>
        <Flex 
          justifyContent="flex-start" 
          mb={6}
          bg={cardBgColor}
          p={4}
          borderRadius="md"
          boxShadow="sm"
          borderWidth="1px"
          borderColor={borderColor}
        >
          <Button
            as={RouterLink}
            to={backLink}
            leftIcon={<Icon as={FaArrowLeft} />}
            colorScheme="green"
            variant="outline"
            size="md"
            _hover={{ bg: 'green.50', _dark: { bg: 'green.900' } }}
          >
            {backText}
          </Button>
        </Flex>
        
        <MarkdownViewer 
          filePath={markdownPath} 
          title={pageTitle} 
        />
        
        <Flex 
          justifyContent="center" 
          mt={12} 
          mb={8}
        >
          <Button
            as={RouterLink}
            to={backLink}
            leftIcon={<Icon as={FaHome} />}
            colorScheme="green"
            size="lg"
            boxShadow="md"
            _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
            transition="all 0.2s"
          >
            Return to Home
          </Button>
        </Flex>
      </Container>

      <Divider borderColor={borderColor} />

      {/* Footer */}
      <Box 
        as="footer" 
        textAlign="center" 
        py={8} 
        px={4}
        borderTopWidth="1px" 
        borderColor={borderColor}
        bg={cardBgColor}
      >
        <Container maxW="container.lg">
          <Flex 
            justifyContent="center" 
            gap={8} 
            flexWrap="wrap" 
            fontSize="sm" 
            fontWeight="medium"
            mb={6}
            color={useColorModeValue('gray.600', 'gray.400')}
          >
            <Link as={RouterLink} to="/privacy" _hover={{ color: accentColor, textDecoration: 'none' }}>Privacy Policy</Link>
            <Link as={RouterLink} to="/terms" _hover={{ color: accentColor, textDecoration: 'none' }}>Terms of Service</Link>
            <Link as={RouterLink} to="/refund" _hover={{ color: accentColor, textDecoration: 'none' }}>Refund Policy</Link>
            <Link as={RouterLink} to="/pricing" _hover={{ color: accentColor, textDecoration: 'none' }}>Pricing</Link>
          </Flex>
          <Box fontSize="sm" color={useColorModeValue('gray.500', 'gray.500')}>
            © {new Date().getFullYear()} SyncdIt. All rights reserved.
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default StaticPage; 